// Firebase database services.
//
// Uses the Firestore *lite* SDK: plain REST reads/writes, no realtime channel and
// no offline cache. That is all this site needs, and it is a fraction of the size
// of the full SDK that every visitor would otherwise download.
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore/lite";
import { getFirebaseApp } from "./init";

// Created on first use so that importing this module has no side effects.
const db = () => getFirestore(getFirebaseApp());

// Writes must carry the signed-in admin's token. Firestore picks it up from Auth
// on the same app, so make sure Auth is initialised first. (In practice it
// already is, because only the admin area writes; this keeps that guarantee
// explicit without pulling the Auth SDK into the public bundle.)
const ensureAuth = async () => {
  const { getAuth } = await import("firebase/auth");
  getAuth(getFirebaseApp());
};

// Get all page content
export const getAllPages = async () => {
  try {
    const pagesCollection = collection(db(), "pages");
    const snapshot = await getDocs(pagesCollection);

    if (snapshot.empty) {
      return [];
    }

    const pages = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    return pages;
  } catch (error) {
    console.error("Error getting pages: ", error);
    throw error; // Re-throw to allow proper error handling in components
  }
};

// Get specific page content
export const getPage = async (pageId) => {
  try {
    const pageDoc = doc(db(), "pages", pageId);
    const snapshot = await getDoc(pageDoc);

    if (snapshot.exists()) {
      return {
        id: snapshot.id,
        ...snapshot.data(),
      };
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error getting page: ", error);
    return null;
  }
};

// Update page content
export const updatePage = async (pageId, pageData) => {
  try {
    // Ensure we have a valid page ID
    if (!pageId) {
      console.error("No page ID provided");
      return false;
    }

    await ensureAuth();
    const pageDoc = doc(db(), "pages", pageId);

    // Create update data with timestamp
    const updateData = {
      ...pageData,
      updatedAt: serverTimestamp(),
    };

    // Perform the update operation
    await updateDoc(pageDoc, updateData);

    return true;
  } catch (error) {
    console.error("Error updating page:", error);
    return false;
  }
};

// Create new page
export const createPage = async (pageId, pageData) => {
  try {
    await ensureAuth();
    const pageDoc = doc(db(), "pages", pageId);

    await setDoc(pageDoc, {
      ...pageData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return true;
  } catch (error) {
    console.error("Error creating page: ", error);
    return false;
  }
};

// Delete a page
export const deletePage = async (pageId) => {
  try {
    await ensureAuth();
    const pageDoc = doc(db(), "pages", pageId);
    await deleteDoc(pageDoc);
    return true;
  } catch (error) {
    console.error("Error deleting page: ", error);
    return false;
  }
};
